import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import {
  customersAPI,
  PerformanceMatrixRow,
  CustomerRatingThreshold,
} from '../../lib/api';
import { useView } from '../../contexts/ViewContext';

interface CustomerPerformanceMatrixViewProps {
  customers: unknown[]; // Not used — we fetch our own data
  ratingThresholds: CustomerRatingThreshold[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** Solid background colors matching the old portal rating badges */
function getRatingStyle(rating: string): React.CSSProperties {
  switch (rating) {
    case 'A': return { backgroundColor: '#4caf50', color: '#fff' }; // green
    case 'B': return { backgroundColor: '#26a69a', color: '#fff' }; // teal
    case 'C': return { backgroundColor: '#ffc107', color: '#fff' }; // amber/gold
    case 'D': return { backgroundColor: '#ff9800', color: '#fff' }; // orange
    case 'E': return { backgroundColor: '#f44336', color: '#fff' }; // red
    case 'F': return { backgroundColor: '#d32f2f', color: '#fff' }; // dark red
    default: return {};
  }
}

export default function CustomerPerformanceMatrixView({
  ratingThresholds,
}: CustomerPerformanceMatrixViewProps) {
  const navigate = useNavigate();
  const { getViewQueryParams, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId } = useView();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PerformanceMatrixRow[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const viewParams = getViewQueryParams();
    customersAPI.getPerformanceMatrix({ year, ...viewParams }).then((res) => {
      if (!cancelled) {
        setRows(res.data.rows);
        setTotal(res.data.total);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [year, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId]);

  // Legend from thresholds
  const legend = useMemo(() => {
    if (ratingThresholds.length === 0) return null;
    return [...ratingThresholds].sort((a, b) => a.order - b.order);
  }, [ratingThresholds]);

  const RatingCell = ({ rating, pct }: { rating: string; pct: number | null }) => {
    if (rating === '-') {
      return null;
    }
    return (
      <span
        className="inline-flex items-center justify-center font-bold text-xs rounded shadow-sm"
        style={{ ...getRatingStyle(rating), width: '28px', height: '28px' }}
        title={pct !== null ? `${pct.toFixed(1)}%` : undefined}
      >
        {rating}
      </span>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span>Performance Matrix ({total} suppliers)</span>
          <div className="flex items-center gap-3">
            {legend && legend.length > 0 && (
              <div className="flex items-center gap-2 mr-4">
                {legend.map((t) => (
                  <div key={t.id} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} />
                    <span className="text-xs text-muted-foreground">{t.grade}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setYear((y) => y - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold min-w-[50px] text-center">{year}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No data for {year}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2.5 text-left font-semibold text-xs sticky left-0 bg-muted/50 z-10" style={{ minWidth: '220px' }}>
                    Supplier
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold text-xs" style={{ minWidth: '100px' }}>
                    Supplier Code
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold text-xs" style={{ minWidth: '140px' }}>
                    Customer
                  </th>
                  {MONTHS.map((m) => (
                    <th key={m} className="px-2 py-2.5 text-center font-semibold text-xs" style={{ minWidth: '50px' }}>
                      {m}
                    </th>
                  ))}
                  <th className="px-2 py-2.5 text-center font-semibold text-xs border-l" style={{ minWidth: '80px' }}>
                    Overall Rating
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.customer_id}
                    className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/customers/${row.customer_id}`)}
                  >
                    <td className="px-3 py-2.5 sticky left-0 bg-background z-10">
                      <span className="text-sm truncate max-w-[210px] block" title={row.Customer}>
                        {row.Customer}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-sm text-muted-foreground">{row.CustomerCode || '-'}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-sm block" style={{ wordWrap: 'break-word', maxWidth: '180px' }}>
                        {row.Business || '-'}
                      </span>
                    </td>
                    {MONTHS.map((m) => {
                      const rating = row[m as keyof PerformanceMatrixRow] as string;
                      const pctKey = `${m}_pct` as keyof PerformanceMatrixRow;
                      const pct = row[pctKey] as number | null;
                      return (
                        <td key={m} className="px-2 py-2.5 text-center">
                          <RatingCell rating={rating} pct={pct} />
                        </td>
                      );
                    })}
                    <td className="px-2 py-2.5 text-center border-l">
                      <RatingCell rating={row.OverallRating} pct={row.OverallPct} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
